(() => {
  const mobileQuery = window.matchMedia('(max-width: 999px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const syncResponsiveButtons = () => {
    document.querySelectorAll('[data-mobile-content], [data-mobile-href]').forEach((button) => {
      const content = button.querySelector('.button__content');

      if (!button.dataset.desktopHref) {
        button.dataset.desktopHref = button.getAttribute('href') || '';
      }

      if (content && !button.dataset.desktopContent) {
        button.dataset.desktopContent = content.textContent.trim();
      }

      if (mobileQuery.matches) {
        if (content && button.dataset.mobileContent) {
          content.textContent = button.dataset.mobileContent;
        }

        if (button.dataset.mobileHref) {
          button.setAttribute('href', button.dataset.mobileHref);
        }
      } else {
        if (content && button.dataset.desktopContent) {
          content.textContent = button.dataset.desktopContent;
        }

        if (button.dataset.desktopHref) {
          button.setAttribute('href', button.dataset.desktopHref);
        }
      }
    });
  };

  const initMobileCarouselAutoplay = () => {
    const autoplayQuery = window.matchMedia('(max-width: 749px)');
    const railSelectors = [
      '.pearhaus-home-trending__track',
      '.pearhaus-difference__features',
      '.pearhaus-routine__steps',
      '.pearhaus-proof__rail'
    ];

    document.querySelectorAll(railSelectors.join(',')).forEach((rail) => {
      if (rail.dataset.pearhausAutoplay === 'ready') return;

      const cards = Array.from(rail.children).filter((card) => card.matches('article, li'));
      if (cards.length < 2) return;

      rail.dataset.pearhausAutoplay = 'ready';

      let currentIndex = 0;
      let direction = 1;
      let timer;
      let resumeTimer;
      let isVisible = false;
      let isInteracting = false;

      const stop = () => {
        window.clearTimeout(timer);
        timer = undefined;
      };

      const cardOffset = (index) => cards[index].offsetLeft - rail.offsetLeft;

      const nearestIndex = () => {
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        cards.forEach((card, index) => {
          const distance = Math.abs(cardOffset(index) - rail.scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      };

      const canPlay = () => (
        autoplayQuery.matches
        && !reducedMotionQuery.matches
        && document.visibilityState === 'visible'
        && isVisible
        && !isInteracting
      );

      const schedule = () => {
        stop();
        if (!canPlay()) return;

        timer = window.setTimeout(() => {
          currentIndex = nearestIndex();

          if (currentIndex >= cards.length - 1) direction = -1;
          if (currentIndex <= 0) direction = 1;

          currentIndex += direction;
          rail.scrollTo({ left: cardOffset(currentIndex), behavior: 'smooth' });
          schedule();
        }, 2000);
      };

      const pauseForInteraction = () => {
        isInteracting = true;
        stop();
        window.clearTimeout(resumeTimer);
      };

      const resumeAfterInteraction = () => {
        window.clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(() => {
          isInteracting = false;
          currentIndex = nearestIndex();
          schedule();
        }, 6500);
      };

      rail.addEventListener('pointerdown', pauseForInteraction, { passive: true });
      rail.addEventListener('pointerup', resumeAfterInteraction, { passive: true });
      rail.addEventListener('pointercancel', resumeAfterInteraction, { passive: true });
      rail.addEventListener('wheel', () => {
        pauseForInteraction();
        resumeAfterInteraction();
      }, { passive: true });
      rail.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        pauseForInteraction();
        resumeAfterInteraction();
      });
      rail.addEventListener('focusin', pauseForInteraction);
      rail.addEventListener('focusout', resumeAfterInteraction);

      rail.querySelectorAll('video').forEach((video) => {
        video.addEventListener('play', pauseForInteraction);
        video.addEventListener('pause', resumeAfterInteraction);
        video.addEventListener('ended', resumeAfterInteraction);
      });

      const visibilityObserver = new IntersectionObserver((entries) => {
        isVisible = entries[0]?.isIntersecting && entries[0].intersectionRatio >= 0.35;
        schedule();
      }, { threshold: [0, 0.35, 0.7] });

      visibilityObserver.observe(rail);
      autoplayQuery.addEventListener('change', schedule);
      reducedMotionQuery.addEventListener('change', schedule);
      document.addEventListener('visibilitychange', schedule);
    });
  };

  const initReviewShowcase = () => {
    const section = document.querySelector('[id$="__178518690496d531ab"]');
    const carousel = section?.querySelector('.jdgm-cards-carousel');

    if (!carousel || carousel.dataset.pearhausShowcase === 'ready') return false;

    const sourceCards = Array.from(
      carousel.querySelectorAll('.jdgm-card:not(.jdgm-clone-before):not(.jdgm-clone-after)')
    );

    if (!sourceCards.length) return false;

    const getMediaElement = (card) => {
      const candidates = Array.from(card.querySelectorAll('video, img'));

      return candidates.find((media) => {
        const className = media.className?.toString().toLowerCase() || '';
        const source = media.poster || media.currentSrc || media.getAttribute('src') || media.dataset?.src || '';

        return source && !className.includes('avatar') && !className.includes('verified');
      });
    };

    const normalizeMedia = (card) => {
      const media = getMediaElement(card);
      const source = media?.poster
        || media?.currentSrc
        || media?.getAttribute('src')
        || media?.dataset?.src
        || media?.querySelector?.('source')?.src
        || '';

      if (!source) return '';

      try {
        const url = new URL(source, window.location.origin);
        const pathname = decodeURIComponent(url.pathname)
          .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|[0-9]+x[0-9]+)(?=\.[a-z0-9]+$)/i, '')
          .toLowerCase();

        return pathname.split('/').pop() || pathname;
      } catch (_error) {
        return source.split('?')[0].split('/').pop().toLowerCase();
      }
    };

    const seenReviews = new Set();
    const seenMedia = new Set();
    const unrelatedReviewPattern = /shipping protection|liquid bond|eyelash curler|cloud pouch/i;
    const lashReviewPattern = /lash(?:es)?|cluster|cloudset|set\s*&\s*stay|shorty/i;
    const unsuitableMediaPattern = /takes some practice to get them on/i;
    const cards = sourceCards.filter((card) => {
      const review = card.querySelector('.jdgm-text')?.textContent.replace(/\s+/g, ' ').trim().toLowerCase() || '';
      const cardText = card.textContent.replace(/\s+/g, ' ').trim();
      const media = normalizeMedia(card);

      if (!media) return false;
      if (unrelatedReviewPattern.test(cardText)) return false;
      if (!lashReviewPattern.test(cardText)) return false;
      if (unsuitableMediaPattern.test(review)) return false;
      if ((review && seenReviews.has(review)) || (media && seenMedia.has(media))) return false;

      if (review) seenReviews.add(review);
      if (media) seenMedia.add(media);
      return true;
    });

    if (!cards.length) return false;

    carousel.dataset.pearhausShowcase = 'ready';
    carousel.classList.add('is-pearhaus-enhanced');

    const showcase = document.createElement('div');
    showcase.className = 'pearhaus-review-showcase';
    showcase.setAttribute('role', 'region');
    showcase.setAttribute('aria-label', 'Customer reviews');

    const previousButton = document.createElement('button');
    previousButton.className = 'pearhaus-review-showcase__control pearhaus-review-showcase__control--previous';
    previousButton.type = 'button';
    previousButton.setAttribute('aria-label', 'Show previous reviews');
    previousButton.innerHTML = '<span aria-hidden="true">&#8592;</span>';

    const grid = document.createElement('div');
    grid.className = 'pearhaus-review-showcase__grid';

    const nextButton = document.createElement('button');
    nextButton.className = 'pearhaus-review-showcase__control pearhaus-review-showcase__control--next';
    nextButton.type = 'button';
    nextButton.setAttribute('aria-label', 'Show next reviews');
    nextButton.innerHTML = '<span aria-hidden="true">&#8594;</span>';

    showcase.append(previousButton, grid, nextButton);
    carousel.insertAdjacentElement('afterend', showcase);

    const mobileReviews = window.matchMedia('(max-width: 699px)');
    const tabletReviews = window.matchMedia('(min-width: 700px) and (max-width: 999px)');
    const wideReviews = window.matchMedia('(min-width: 1280px)');
    let currentIndex = 0;
    let timer;
    let paused = false;

    const getPageSize = () => (mobileReviews.matches ? 1 : tabletReviews.matches ? 2 : wideReviews.matches ? 4 : 3);

    const createReviewCard = (sourceCard) => {
      const card = document.createElement('article');
      card.className = 'pearhaus-review-card';

      const mediaWrap = document.createElement('div');
      mediaWrap.className = 'pearhaus-review-card__media';
      const sourceMedia = getMediaElement(sourceCard);

      if (sourceMedia) {
        const media = sourceMedia.cloneNode(true);
        media.removeAttribute('class');
        media.removeAttribute('style');
        media.removeAttribute('width');
        media.removeAttribute('height');
        media.loading = 'lazy';

        if (media.tagName === 'VIDEO') {
          media.controls = true;
          media.playsInline = true;
          media.preload = 'metadata';
        } else {
          media.decoding = 'async';
          media.alt = media.alt || 'Pearhaus customer lash result';
        }

        mediaWrap.append(media);
      }

      const body = document.createElement('div');
      body.className = 'pearhaus-review-card__body';

      const reviewText = sourceCard.querySelector('.jdgm-text')?.textContent.replace(/\s+/g, ' ').trim();
      if (reviewText) {
        const quote = document.createElement('p');
        quote.className = 'pearhaus-review-card__quote';
        quote.textContent = reviewText;
        body.append(quote);
      }

      const sourceRating = sourceCard.querySelector('.jdgm-rating-section');
      if (sourceRating) {
        const rating = sourceRating.cloneNode(true);
        rating.className = 'pearhaus-review-card__meta';
        rating.removeAttribute('style');
        body.append(rating);
      }

      card.append(mediaWrap, body);
      return card;
    };

    const render = () => {
      const pageSize = getPageSize();
      const visibleCards = Array.from(
        { length: Math.min(pageSize, cards.length) },
        (_, offset) => createReviewCard(cards[(currentIndex + offset) % cards.length])
      );

      grid.replaceChildren(...visibleCards);
      grid.style.setProperty('--pearhaus-review-columns', String(visibleCards.length));
      showcase.dataset.visibleCards = String(visibleCards.length);
    };

    const move = (direction) => {
      const pageSize = getPageSize();
      currentIndex = (currentIndex + direction * pageSize + cards.length) % cards.length;
      render();
    };

    const stop = () => {
      window.clearInterval(timer);
      timer = undefined;
    };

    const play = () => {
      stop();

      if (paused || reducedMotionQuery.matches) return;

      timer = window.setInterval(() => {
        if (document.visibilityState === 'visible' && !paused) {
          move(1);
        }
      }, 28000);
    };

    const pause = () => {
      paused = true;
      stop();
    };

    const resume = () => {
      paused = false;
      play();
    };

    previousButton.addEventListener('click', () => {
      move(-1);
      play();
    });
    nextButton.addEventListener('click', () => {
      move(1);
      play();
    });
    showcase.addEventListener('mouseenter', pause);
    showcase.addEventListener('mouseleave', resume);
    showcase.addEventListener('focusin', pause);
    showcase.addEventListener('focusout', resume);
    mobileReviews.addEventListener('change', render);
    tabletReviews.addEventListener('change', render);
    wideReviews.addEventListener('change', render);
    reducedMotionQuery.addEventListener('change', play);
    document.addEventListener('visibilitychange', play);
    render();
    play();

    return true;
  };

  const initHomepage = () => {
    syncResponsiveButtons();
    initMobileCarouselAutoplay();

    if (initReviewShowcase()) return;

    const observer = new MutationObserver(() => {
      if (initReviewShowcase()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  mobileQuery.addEventListener('change', syncResponsiveButtons);
  document.addEventListener('shopify:section:load', initHomepage);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHomepage, { once: true });
  } else {
    initHomepage();
  }
})();
